import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { getConfigValue } from '../services/systemConfigurationService';
import Icon from './AppIcon';

const GoogleMapComponent = ({ 
  latitude = 31.9454, 
  longitude = 35.9284, 
  onLocationSelect,
  draggable = false,
  zoom = 14,
  height = '400px'
}) => {
  const [markerPosition, setMarkerPosition] = useState({
    lat: parseFloat(latitude),
    lng: parseFloat(longitude)
  });
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        // Try to get from database first
        const dbKey = await getConfigValue('google_maps_api_key');
        if (dbKey && dbKey?.trim() !== '') {
          setApiKey(dbKey);
        } else {
          // Fallback to environment variable
          const envKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
          if (envKey && envKey !== 'your-google-maps-api-key-here') {
            setApiKey(envKey);
          }
        }
      } catch (error) {
        console.error('Error fetching API key:', error);
        // Fallback to environment variable on error
        const envKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
        if (envKey && envKey !== 'your-google-maps-api-key-here') {
          setApiKey(envKey);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchApiKey();
  }, []);

  const mapContainerStyle = {
    width: '100%',
    height: height
  };

  const center = {
    lat: parseFloat(latitude),
    lng: parseFloat(longitude)
  };

  const handleMapClick = useCallback((event) => {
    if (draggable && event?.latLng) {
      const newPosition = {
        lat: event?.latLng?.lat(),
        lng: event?.latLng?.lng()
      };
      setMarkerPosition(newPosition);
      if (onLocationSelect) {
        onLocationSelect(newPosition);
      }
    }
  }, [draggable, onLocationSelect]);

  const handleMarkerDragEnd = useCallback((event) => {
    if (event?.latLng) {
      const newPosition = {
        lat: event?.latLng?.lat(),
        lng: event?.latLng?.lng()
      };
      setMarkerPosition(newPosition);
      if (onLocationSelect) {
        onLocationSelect(newPosition);
      }
    }
  }, [onLocationSelect]);

  if (loading) {
    return (
      <div 
        className="w-full rounded-lg border border-border bg-muted/30 flex items-center justify-center"
        style={{ height: height }}
      >
        <div className="text-center">
          <Icon name="Loader" className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">جاري تحميل الخريطة...</p>
        </div>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div 
        className="w-full rounded-lg border border-border bg-muted/30 flex items-center justify-center text-muted-foreground text-sm"
        style={{ height: height }}
      >
        <div className="text-center p-4">
          <Icon name="MapPin" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="mb-2 font-semibold">مفتاح Google Maps API غير متوفر</p>
          <p className="text-xs">يرجى إضافة مفتاح Google Maps API من لوحة إعدادات النظام</p>
          <p className="text-xs mt-2">إدارة النظام والمستخدمين → إعدادات النظام → التكاملات الخارجية</p>
        </div>
      </div>
    );
  }

  return (
    <LoadScript googleMapsApiKey={apiKey} language="ar">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true
        }}
      >
        <Marker
          position={markerPosition}
          draggable={draggable}
          onDragEnd={handleMarkerDragEnd}
        />
      </GoogleMap>
    </LoadScript>
  );
};

export default GoogleMapComponent;